/**
 * Payment funnel tracker.
 *
 * Fire-and-forget helper that logs meaningful payment lifecycle events to
 * `/api/tracking/payment-event` so admin can see:
 *   - How many CTAs clicked
 *   - How many actually opened Razorpay Checkout
 *   - How many completed vs abandoned vs failed
 *
 * Never throws, never blocks the UI. Enrich the payload with acquisition
 * context automatically via the referral tracker so we know which page /
 * campaign the funnel event came from.
 */

import axios from 'axios';
import { getReferralContext } from './referralTracker';
import { trackEvent, EVENTS } from './analytics';

const BACKEND = process.env.REACT_APP_BACKEND_URL || '';

const authHeaders = () => {
  if (typeof window === 'undefined') return {};
  try {
    const t = window.localStorage.getItem('le_token');
    return t ? { Authorization: `Bearer ${t}` } : {};
  } catch (_) {
    return {};
  }
};

const currentPath = () => {
  if (typeof window === 'undefined') return null;
  try {
    return window.location.pathname + (window.location.search || '');
  } catch (_) {
    return null;
  }
};

/**
 * Fire a payment funnel event. All fields except `event_type` are optional.
 *
 * @param {Object} p
 * @param {'click'|'checkout_started'|'checkout_completed'|'checkout_abandoned'|'checkout_failed'} p.event_type
 * @param {'one_time'|'gold'|'enterprise'} [p.plan_type]
 * @param {string} [p.cta_label]  e.g. 'pricing_one_time_buy'
 * @param {string} [p.guest_email]
 * @param {string} [p.guest_name]
 * @param {number} [p.amount_paise]
 * @param {string} [p.currency]
 * @param {string} [p.razorpay_order_id]
 * @param {string} [p.razorpay_subscription_id]
 * @param {string} [p.razorpay_payment_id]
 * @param {string} [p.reason]
 */
export function trackPaymentEvent(p = {}) {
  if (!p || !p.event_type) return;
  try {
    const payload = {
      event_type: p.event_type,
      plan_type: p.plan_type,
      cta_label: p.cta_label,
      source_path: p.source_path || currentPath(),
      guest_email: p.guest_email,
      guest_name: p.guest_name,
      amount_paise: p.amount_paise,
      currency: p.currency,
      razorpay_order_id: p.razorpay_order_id,
      razorpay_subscription_id: p.razorpay_subscription_id,
      razorpay_payment_id: p.razorpay_payment_id,
      reason: p.reason,
      referral: getReferralContext(),
    };
    // Strip undefined so the backend Pydantic model doesn't choke.
    Object.keys(payload).forEach((k) => payload[k] === undefined && delete payload[k]);
    axios
      .post(`${BACKEND}/api/tracking/payment-event`, payload, {
        headers: authHeaders(),
        timeout: 6000,
      })
      .catch(() => {});

    // Mirror lifecycle events to GA4 so the admin's Reports tab and
    // GA4 Explore reports share one funnel definition. We map our
    // internal event_type onto GA4-recommended event names so key
    // events can be flagged in the GA4 UI without renaming.
    const ga4Value = p.amount_paise ? Number(p.amount_paise) / 100 : undefined;
    const commonGa4 = {
      plan: p.plan_type,
      currency: p.currency,
      value: ga4Value,
      order_id: p.razorpay_order_id,
      payment_id: p.razorpay_payment_id,
    };
    if (p.event_type === 'checkout_started') {
      trackEvent(EVENTS.CHECKOUT_OPEN, commonGa4);
    } else if (p.event_type === 'checkout_completed') {
      trackEvent(EVENTS.PURCHASE, {
        ...commonGa4,
        transaction_id: p.razorpay_payment_id || p.razorpay_order_id,
      });
    } else if (p.event_type === 'checkout_abandoned') {
      trackEvent(EVENTS.CHECKOUT_DISMISSED, { ...commonGa4, reason: p.reason });
    } else if (p.event_type === 'click') {
      trackEvent(EVENTS.CTA_CLICK, { cta: p.cta_label || 'checkout_click', plan: p.plan_type });
    }
  } catch (_) {
    /* swallow */
  }
}
